'use client';

import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useFeatureFlagsContext } from '@/@feature-flags/context';
import { useSession } from '@/providers/enhanced-session';

import { activityCsvFileName, creditLedgerService, saveBlob } from '../ledger-services';
import { creditConsumptionService } from '../services';
import type {
    CreditActivityPage,
    CreditEarlierActivity,
    CreditHolds,
    CreditUsage,
} from '../types/activity';
import type { CreditBalance } from '../types/balance';
import { FEATURE_FLAG_KEY, QUERY_KEYS } from '../types/constants';
import type { IClientCreditConsumptionSummary } from '../types/entities';
import {
    ACTIVITY_PAGE_SIZE,
    LEDGER_READ_QUERY_KEYS,
    type CreditActivityQuery,
    type CreditEarlierQuery,
    type CreditFleetBalances,
    type CreditFleetQuery,
    type CreditReadTarget,
    type CreditUsageQuery,
} from '../types/ledger-reads';
import { useCreditsViewer, type CreditsViewer } from './useCreditsViewer';

/**
 * The reads on the new balance (M4S-5895, plan step 24). docs/pdlc/credits-ui.md §2.4, §2.6, §7.
 *
 * - **Whose credits.** `creditReadTargetOf(viewer)`: the person's own org in client mode; the
 *   client's org with `view: 'client'` under View-as (D13, exactly the client admin's answer); none
 *   while a View-as session is pending, so nobody's credits are fetched then (B11).
 * - **Which balance.** The UI never reads `credits-ledger-balance` (§2.6): it reads `reader` from
 *   the balance response. Usage, activity, earlier activity, holds and the CSV are reads of the
 *   new balance, so each is `enabled` only when that org's `reader` is `'ledger'`. An unflipped
 *   org (every org while the switches are off) never calls them and keeps today's screens.
 * - **The Credits tab.** `creditsTab` from the balance response decides it (D17), `useCreditsTab`,
 *   read once per mount and never on an interval or on refocus.
 */

/** Pure: whose credits the screens read for this viewer; null while View-as is pending. */
export function creditReadTargetOf(viewer: CreditsViewer): CreditReadTarget | null {
    if (viewer.mode === 'pending') return null;
    if (viewer.mode === 'view-as') {
        return viewer.orgId ? { kind: 'org', orgId: viewer.orgId, view: 'client' } : null;
    }
    return { kind: 'own' };
}

/** The read target for the signed-in person in this tab. */
export function useCreditReadTarget(): CreditReadTarget | null {
    const { mode, orgId } = useCreditsViewer();
    // Primitive deps keep one reference (and so one query key) while the answer is unchanged.
    return useMemo(
        () => creditReadTargetOf({ mode, orgId, orgName: null, canSeeNames: false }),
        [mode, orgId],
    );
}

/** Pure: whether the reads on the new balance may run for an org with this reader. */
export function ledgerReadsEnabled(reader: CreditBalance['reader'] | null | undefined): boolean {
    return reader === 'ledger';
}

function balanceKey(target: CreditReadTarget | null) {
    if (!target) return [...LEDGER_READ_QUERY_KEYS.BALANCE, 'none'] as const;
    return target.kind === 'own'
        ? LEDGER_READ_QUERY_KEYS.BALANCE
        : LEDGER_READ_QUERY_KEYS.ORG_BALANCE(target.orgId);
}

/**
 * The one balance read for a target (§2.4). No retry: before the ledger migration the API answers
 * 503 `LEDGER_NOT_READY`, which is an answer, not a blip, and every caller falls back to today's.
 */
export function useCreditBalance(
    target: CreditReadTarget | null,
    enabled = true,
    freshness: ReadFreshness = LIVE_READ,
) {
    return useQuery<CreditBalance, Error>({
        queryKey: balanceKey(target),
        queryFn: () => creditLedgerService.getBalanceFor(target as CreditReadTarget),
        enabled: enabled && target != null,
        retry: false,
        ...freshness,
    });
}

/** How often a read is repeated: on refocus once a minute old, or once per mount. */
export interface ReadFreshness {
    staleTime: number;
    refetchOnWindowFocus: boolean;
}

/** The balance screens' own read: fresh within a minute, re-read when the window regains focus. */
export const LIVE_READ: ReadFreshness = { staleTime: 1000 * 60, refetchOnWindowFocus: true };

/**
 * A read that decides only whether something shows (the Credits tab): once per mount, never on an
 * interval or on refocus. Five minutes matches the API's flag cache, so a flip shows no later than
 * the server itself would see it.
 */
export const ONCE_PER_MOUNT: ReadFreshness = {
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
};

/** The balance for whoever this tab is showing credits for (the client's, under View-as). */
export function useViewerCreditBalance(enabled = true) {
    const target = useCreditReadTarget();
    return { target, ...useCreditBalance(target, enabled) };
}

/**
 * The person's own org summary (today's `/credit-consumption`, the dashboard's and chat's read),
 * read once for the Credits tab. Its `ledger` block is sent only for an org on the new balance
 * (M4S-5910, `withLedgerBlock`) and is that org's balance, `creditsTab` included, so outside
 * View-as the tab needs no balance read at all.
 */
function useOwnSummaryForTab(enabled: boolean) {
    return useQuery<IClientCreditConsumptionSummary, Error>({
        queryKey: QUERY_KEYS.SUMMARY(),
        queryFn: () => creditConsumptionService.getSummary(),
        enabled,
        retry: false,
        ...ONCE_PER_MOUNT,
    });
}

/** The fleet: every org, flipped or not, filtered on the server (staff pages only). */
export function useFleetBalances(query: CreditFleetQuery = {}, enabled = true) {
    return useQuery<CreditFleetBalances, Error>({
        queryKey: LEDGER_READ_QUERY_KEYS.FLEET_BALANCES(query),
        queryFn: () => creditLedgerService.getFleetBalances(query),
        enabled,
        staleTime: 1000 * 60,
        retry: false,
        placeholderData: (prev) => prev,
    });
}

/** Options shared by the reads of the new balance. */
export interface LedgerReadOptions {
    /** The org's `reader` from its balance response; the read runs only for `'ledger'`. */
    reader: CreditBalance['reader'] | null | undefined;
    enabled?: boolean;
}

function readEnabled(target: CreditReadTarget | null, options: LedgerReadOptions): boolean {
    return (options.enabled ?? true) && target != null && ledgerReadsEnabled(options.reader);
}

export function useCreditUsage(
    target: CreditReadTarget | null,
    query: CreditUsageQuery,
    options: LedgerReadOptions,
) {
    return useQuery<CreditUsage, Error>({
        queryKey: LEDGER_READ_QUERY_KEYS.USAGE(target ?? { kind: 'own' }, query),
        queryFn: () => creditLedgerService.getUsage(target as CreditReadTarget, query),
        enabled: readEnabled(target, options),
        staleTime: 1000 * 60 * 5,
        retry: false,
    });
}

export function useCreditActivity(
    target: CreditReadTarget | null,
    query: CreditActivityQuery,
    options: LedgerReadOptions,
) {
    const withPage: CreditActivityQuery = {
        ...query,
        page: query.page ?? 1,
        limit: query.limit ?? ACTIVITY_PAGE_SIZE,
    };
    return useQuery<CreditActivityPage, Error>({
        queryKey: LEDGER_READ_QUERY_KEYS.ACTIVITY(target ?? { kind: 'own' }, withPage),
        queryFn: () => creditLedgerService.getActivity(target as CreditReadTarget, withPage),
        enabled: readEnabled(target, options),
        staleTime: 1000 * 60 * 5,
        retry: false,
        placeholderData: (prev) => prev,
    });
}

/** "Earlier activity (before {T0})". */
export function useEarlierCreditActivity(
    target: CreditReadTarget | null,
    query: CreditEarlierQuery,
    options: LedgerReadOptions,
) {
    const withPage: CreditEarlierQuery = {
        page: query.page ?? 1,
        limit: query.limit ?? ACTIVITY_PAGE_SIZE,
    };
    return useQuery<CreditEarlierActivity, Error>({
        queryKey: LEDGER_READ_QUERY_KEYS.ACTIVITY_EARLIER(target ?? { kind: 'own' }, withPage),
        queryFn: () => creditLedgerService.getEarlierActivity(target as CreditReadTarget, withPage),
        enabled: readEnabled(target, options),
        staleTime: 1000 * 60 * 5,
        retry: false,
        placeholderData: (prev) => prev,
    });
}

/** Fielding holds (§6, §7 block 6). */
export function useCreditHolds(target: CreditReadTarget | null, options: LedgerReadOptions) {
    return useQuery<CreditHolds, Error>({
        queryKey: LEDGER_READ_QUERY_KEYS.HOLDS(target ?? { kind: 'own' }),
        queryFn: () => creditLedgerService.getHolds(target as CreditReadTarget),
        enabled: readEnabled(target, options),
        staleTime: 1000 * 60,
        retry: false,
    });
}

/**
 * "Download activity (CSV)": fetched from the server and handed to the browser to save. Refused
 * (never sent) for no target or an org not on the new balance, as the reads are.
 */
export function useDownloadActivityCsv(
    target: CreditReadTarget | null,
    options: LedgerReadOptions,
) {
    const canDownload = readEnabled(target, options);
    const mutation = useMutation<void, Error, CreditActivityQuery | void>({
        mutationFn: async (query) => {
            if (!target || !canDownload) {
                throw new Error('Activity downloads are not available for this account yet.');
            }
            const blob = await creditLedgerService.downloadActivityCsv(target, query ?? {});
            saveBlob(blob, activityCsvFileName(target));
        },
    });
    return { ...mutation, canDownload };
}

/** What decides the Credits tab, as `creditsTabState` reads it. */
export interface CreditsTabInputs {
    /** A View-as session is restoring, starting or ending: nobody's answer is known yet. */
    viewerPending: boolean;
    /** Staff viewing as a client: the balance is the client's, and the browser's flags are not. */
    viewAs: boolean;
    balance: CreditBalance | undefined;
    balanceLoading: boolean;
    /** The balance read failed: before the ledger migration (503), or an API without it (404). */
    balanceFailed: boolean;
    /** Today's check: the `credit-consumption` flag as the browser evaluates it. */
    flag: boolean;
    flagsLoading: boolean;
}

export interface CreditsTabState {
    show: boolean;
    isLoading: boolean;
    /** Where the answer came from: the balance response, or today's flag check. */
    source: 'balance' | 'flag' | 'pending';
}

/**
 * Pure: whether the Credits tab shows (D17). The balance response's `creditsTab` decides it; that
 * answer is "on the new balance with a credits account, or the `credit-consumption` flag", read
 * on the server for the org being shown, so under View-as the client's answer decides, not the
 * staff member's flags. Until the balance answers, and whenever it cannot (the ledger migration
 * not yet run, an older API), today's flag check stands.
 *
 * For a person's own org the server's flag half is read by org only
 * (`FeatureFlagsService.hasAccessByOrgId`: no per-person override, no Cognito group, no internal
 * toggle), so it can disagree with the browser's check, which counts all of them. So outside
 * View-as the browser's flag still shows the tab, and the server's answer adds the tab only for an
 * org on the new balance. With every switch off no org is, and the tab is exactly today's.
 */
export function creditsTabState(inputs: CreditsTabInputs): CreditsTabState {
    if (inputs.viewerPending) return { show: false, isLoading: true, source: 'pending' };
    // A body with no `creditsTab` is not the published answer: today's check stands.
    const answer =
        inputs.balance && typeof inputs.balance.creditsTab === 'boolean' ? inputs.balance : null;
    if (inputs.viewAs && answer) {
        return { show: answer.creditsTab, isLoading: false, source: 'balance' };
    }
    if (!inputs.viewAs && !inputs.flag && answer?.reader === 'ledger') {
        return { show: answer.creditsTab, isLoading: false, source: 'balance' };
    }
    if (answer || inputs.balanceFailed || inputs.balance) {
        return { show: inputs.flag, isLoading: inputs.flagsLoading, source: 'flag' };
    }
    return {
        show: inputs.flag,
        isLoading: inputs.balanceLoading || inputs.flagsLoading,
        source: 'flag',
    };
}

/**
 * Pure: which read the Credits tab needs (D17), so that with every switch off nothing new runs.
 *
 * - Under View-as (staff only) the client's balance, since the staff member's flags do not count.
 * - Outside View-as the server's answer can add the tab only for an org on the new balance without
 *   the browser's `credit-consumption` flag (`creditsTabState`). With the flag nothing is read; a
 *   person without it reads the org's summary once, whose `ledger` block is the answer. A freemium
 *   person has no org summary and reads nothing.
 */
export function creditsTabReads(inputs: {
    target: CreditReadTarget | null;
    flag: boolean;
    flagsLoading: boolean;
    hasOrg: boolean;
}): { clientBalance: boolean; ownSummary: boolean } {
    const viewAs = inputs.target?.kind === 'org';
    return {
        clientBalance: viewAs,
        ownSummary:
            inputs.target?.kind === 'own' && inputs.hasOrg && !inputs.flagsLoading && !inputs.flag,
    };
}

/** The Credits tab for this tab's viewer: the tab bar and the page gate both read it. */
export function useCreditsTab(): CreditsTabState & { balance: CreditBalance | undefined } {
    const { hasFlag, isLoading: flagsLoading } = useFeatureFlagsContext();
    const { data: session } = useSession();
    const flag = hasFlag(FEATURE_FLAG_KEY);
    const target = useCreditReadTarget();
    const viewAs = target?.kind === 'org';
    const reads = creditsTabReads({
        target,
        flag,
        flagsLoading,
        hasOrg: !!session?.user && session.user.isUnaffiliated !== true,
    });

    const client = useCreditBalance(target, reads.clientBalance, ONCE_PER_MOUNT);
    const own = useOwnSummaryForTab(reads.ownSummary);
    const read = viewAs ? client : own;
    const balance = viewAs ? client.data : (own.data?.ledger ?? undefined);

    const state = creditsTabState({
        viewerPending: target == null,
        viewAs,
        balance,
        balanceLoading: read.isLoading,
        balanceFailed: read.isError,
        flag,
        flagsLoading,
    });
    return { ...state, balance };
}
